import { useLocation } from "@tanstack/react-router";
import {
	Check,
	ChevronDown,
	ChevronSelectorVertical,
	LogOut01,
	Monitor01,
	Shield01,
	User01,
} from "@untitledui/icons";
import {
	type FC,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { type Placement, useFocusManager } from "react-aria";
import type { DialogProps as AriaDialogProps } from "react-aria-components";
import {
	Button as AriaButton,
	Dialog as AriaDialog,
	DialogTrigger as AriaDialogTrigger,
	Popover as AriaPopover,
	OverlayTriggerStateContext,
} from "react-aria-components";
import { Avatar } from "@/components/Atoms/Avatar/Avatar";
import { AvatarLabelGroup } from "@/components/Atoms/AvatarLabelGroup/AvatarLabelGroup";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { useAppTranslation } from "@/hooks/use-app-translation";
import type { Organization } from "@/services/organization";
import { useAppNavigation } from "@/stores/app-hooks";
import { authStore } from "@/stores/auth-store";
import { cx } from "@/utils/cx";
import type { AccountMode } from "./account-mode";
import { AccountMode as AccountModeConst } from "./account-mode";

export type { AccountMode } from "./account-mode";
export { AccountMode as AccountModeValues } from "./account-mode";

interface SwitchAppOption {
	id: "main" | "admin";
	nameKey: string;
	icon: FC<{ className?: string }>;
}

const SWITCH_APP_OPTIONS: SwitchAppOption[] = [
	{
		id: "admin",
		nameKey: "main.nav.menu.adminPortalApp",
		icon: Shield01,
	},
	{
		id: "main",
		nameKey: "main.nav.menu.applicationApp",
		icon: Monitor01,
	},
];

export const NavMenu = ({
	className,
	selectedId,
	avatarSrc,
	title,
	avatarAlt,
	avatarInitials,
	menuType = "application",
	availableOrganizations,
	currentOrganization,
	disabled,
	mainAppNavCardOnClickHandler,
	adminAppNavCardOnClickHandler,
	isPlatformAdmin = false,
	isOrgAdmin = false,
	accountMode,
	onUserProfile,
	onSignOut,
	...dialogProps
}: AriaDialogProps & {
	className?: string;
	selectedId?: string;
	avatarSrc?: string | FC<{ className?: string }>;
	title?: string;
	avatarAlt?: string;
	avatarInitials?: string;
	menuType?: "application" | "organization";
	availableOrganizations: Organization[];
	currentOrganization: Organization;
	disabled?: boolean;
	mainAppNavCardOnClickHandler?: () => void;
	adminAppNavCardOnClickHandler?: () => void;
	isPlatformAdmin?: boolean;
	isOrgAdmin?: boolean;
	/** Footer account mode — drives Profile / Sign Out / SWITCH APP menu */
	accountMode?: AccountMode;
	onUserProfile?: () => void;
	onSignOut?: () => void;
}) => {
	const focusManager = useFocusManager();
	const dialogRef = useRef<HTMLDivElement>(null);
	const { t } = useAppTranslation();

	// Get the overlay state to control popover closing
	const state = useContext(OverlayTriggerStateContext);

	// Get current location to determine active app
	const location = useLocation();

	// Get navigation functions from hook
	const { switchToOrganization } = useAppNavigation();

	// Where to land after org switch: "all" → admin; from admin → org-admin for that org; from org-admin → org-admin (or admin if "all"); web → stay in web
	const getRedirectToForOrg = (
		orgId: string,
	): "admin" | "org-admin" | undefined => {
		const path = location.pathname;
		if (orgId === "all") return "admin";
		if (path.startsWith("/org-admin")) return "org-admin";
		if (path.startsWith("/admin")) return "org-admin"; // from platform admin, picking an org → org-admin for that org
		return undefined; // web app
	};

	const onKeyDown = useCallback(
		(e: KeyboardEvent) => {
			switch (e.key) {
				case "ArrowDown":
					focusManager?.focusNext({ tabbable: true, wrap: true });
					break;
				case "ArrowUp":
					focusManager?.focusPrevious({ tabbable: true, wrap: true });
					break;
			}
		},
		[focusManager],
	);

	useEffect(() => {
		const element = dialogRef.current;
		if (element) {
			element.addEventListener("keydown", onKeyDown);
		}

		return () => {
			if (element) {
				element.removeEventListener("keydown", onKeyDown);
			}
		};
	}, [onKeyDown]);

	function handleOrganizationSelect(orgId: string) {
		if (isPlatformAdmin || currentOrganization?.id === orgId) {
			switchToOrganization(orgId);
			state?.close();
			return;
		}

		state?.close();
		authStore.getState().redirectToLoginWithOrganization(orgId, {
			redirectTo: getRedirectToForOrg(orgId),
		});
	}

	/** Figma 3970:33784 — Org Switcher DropdownMenu (select items, no search) */
	const renderOrganizationMenu = () => (
		<div
			role="menu"
			aria-label="Organizations"
			className="flex w-full max-h-[400px] flex-col overflow-y-auto"
		>
			{disabled ? null : availableOrganizations.length === 0 ? (
				<div className="px-3 py-4 text-center text-sm text-tertiary">
					No organizations
				</div>
			) : (
				availableOrganizations.map((org) => {
					const isSelected = currentOrganization?.id === org.id;
					return (
						<button
							key={org.id}
							type="button"
							role="menuitemradio"
							aria-checked={isSelected}
							className="group flex h-9 w-full cursor-pointer items-center overflow-clip text-left outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-0"
							onClick={() => handleOrganizationSelect(org.id)}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									handleOrganizationSelect(org.id);
								}
							}}
						>
							<div
								className={cx(
									"flex min-w-px flex-1 items-center gap-3 overflow-clip rounded-md px-3 py-2 transition duration-100 ease-linear",
									isSelected
										? "bg-[rgba(181,71,8,0.02)]"
										: "group-hover:bg-primary_hover",
								)}
							>
								<span
									className={cx(
										"min-w-px flex-1 truncate text-sm font-semibold",
										isSelected
											? "text-primary"
											: "text-secondary group-hover:text-secondary_hover",
									)}
								>
									{org.name}
								</span>
								{isSelected && (
									<Check className="size-4 shrink-0 text-primary" aria-hidden />
								)}
							</div>
						</button>
					);
				})
			)}
		</div>
	);

	const renderApplicationMenu = () => {
		const currentPath = location.pathname;
		const activeAppId =
			currentPath.startsWith("/org-admin") || currentPath.startsWith("/admin")
				? "admin"
				: "main";

		const showSwitchApp = accountMode !== AccountModeConst.Standard;

		const switchHandlers = {
			main: mainAppNavCardOnClickHandler,
			admin: adminAppNavCardOnClickHandler,
		} as const;

		function handleMenuAction(action?: () => void) {
			action?.();
			state?.close();
		}

		return (
			<div className="relative flex w-full flex-col items-stretch">
				{/* Figma DropdownMenu Section 1 — MenuItems h-36, content px-lg py-md */}
				<div className="flex w-full flex-col">
					<button
						type="button"
						className="group flex h-9 w-full cursor-pointer items-center overflow-clip text-left outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-0"
						onClick={() => handleMenuAction(onUserProfile)}
					>
						<div className="flex min-w-px flex-1 items-center gap-3 overflow-clip rounded-md px-3 py-2 transition duration-100 ease-linear group-hover:bg-primary_hover">
							<span className="flex min-w-px flex-1 items-center gap-2">
								<User01 className="size-5 shrink-0 text-fg-quaternary" />
								<span className="min-w-px flex-1 truncate text-sm font-semibold text-secondary group-hover:text-secondary_hover">
									{t("main.nav.menu.userProfile")}
								</span>
							</span>
						</div>
					</button>
					<button
						type="button"
						className="group flex h-9 w-full cursor-pointer items-center overflow-clip text-left outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-0"
						onClick={() => handleMenuAction(onSignOut)}
					>
						<div className="flex min-w-px flex-1 items-center gap-3 overflow-clip rounded-md px-3 py-2 transition duration-100 ease-linear group-hover:bg-primary_hover">
							<span className="flex min-w-px flex-1 items-center gap-2">
								<LogOut01 className="size-5 shrink-0 text-fg-quaternary" />
								<span className="min-w-px flex-1 truncate text-sm font-semibold text-secondary group-hover:text-secondary_hover">
									{t("main.nav.menu.account.signOut")}
								</span>
							</span>
						</div>
					</button>
				</div>

				{showSwitchApp && (
					<div className="flex w-full flex-col">
						<Dropdown.SectionHeader
							title={t("main.nav.menu.switchApp")}
							order="below"
						/>
						{SWITCH_APP_OPTIONS.map((app) => {
							const Icon = app.icon;
							const isActive = activeAppId === app.id;
							return (
								<button
									key={app.id}
									type="button"
									className="group flex h-9 w-full cursor-pointer items-center overflow-clip text-left outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-0"
									onClick={() => handleMenuAction(switchHandlers[app.id])}
								>
									{/* Figma Select MenuItem — selected: alpha-accent-2, px-lg py-md */}
									<div
										className={cx(
											"flex min-w-px flex-1 items-center gap-3 overflow-clip rounded-md px-3 py-2 transition duration-100 ease-linear",
											isActive
												? "bg-[rgba(181,71,8,0.02)]"
												: "group-hover:bg-primary_hover",
										)}
									>
										<span className="flex min-w-px flex-1 items-center gap-2">
											<Icon className="size-5 shrink-0 text-fg-quaternary" />
											<span
												className={cx(
													"min-w-px flex-1 truncate text-sm font-semibold",
													isActive
														? "text-primary"
														: "text-secondary group-hover:text-secondary_hover",
												)}
											>
												{t(app.nameKey)}
											</span>
										</span>
										{isActive && (
											<Check
												className="size-4 shrink-0 text-primary"
												aria-hidden
											/>
										)}
									</div>
								</button>
							);
						})}
					</div>
				)}
			</div>
		);
	};

	return (
		<AriaDialog
			{...dialogProps}
			ref={dialogRef}
			className={cx(
				"w-full rounded-2xl shadow-lg ring ring-secondary_alt outline-hidden bg-primary_alt p-4",
				className,
			)}
		>
			{menuType === "organization"
				? renderOrganizationMenu()
				: renderApplicationMenu()}
		</AriaDialog>
	);
};

export const NavCard = ({
	mainAppNavCardOnClickHandler = () => {},
	adminAppNavCardOnClickHandler = () => {},
	popoverPlacement,
	selectedId,
	avatarSrc,
	avatarAlt = "",
	avatarInitials = "",
	avatarBackgroundColor,
	iconClassName,
	title = "",
	subtitle,
	showSelector = true,
	menuType = "application",
	availableOrganizations,
	currentOrganization,
	disabled,
	isPlatformAdmin = false,
	isOrgAdmin = false,
	variant = "light",
	accountMode,
	onUserProfile,
	onSignOut,
	isOpen: isOpenControlled,
	onOpenChange,
	isCollapsed = false,
}: {
	popoverPlacement?: Placement;
	selectedId?: string;
	avatarSrc?: string | FC<{ className?: string }>;
	avatarAlt?: string;
	avatarInitials?: string;
	avatarBackgroundColor?: string;
	iconClassName?: string;
	title?: string;
	/** Optional subtitle shown below title (e.g. user email on footer card) */
	subtitle?: string;
	showSelector?: boolean;
	menuType?: "application" | "organization";
	availableOrganizations: Organization[];
	currentOrganization: Organization;
	disabled?: boolean;
	mainAppNavCardOnClickHandler?: () => void;
	adminAppNavCardOnClickHandler?: () => void;
	isPlatformAdmin?: boolean;
	isOrgAdmin?: boolean;
	/** "brand" applies on-brand dark surface pill styling */
	variant?: "light" | "brand";
	/** Footer account mode for application menuType */
	accountMode?: AccountMode;
	onUserProfile?: () => void;
	onSignOut?: () => void;
	/** Controlled open state for exclusive sidebar menus */
	isOpen?: boolean;
	onOpenChange?: (isOpen: boolean) => void;
	/**
	 * Collapsed sidebar mark (Figma Nav footer 3940:41078) — avatar-only
	 * circle, no label/chevron. Account: 54×56 pill; org: compact 44px.
	 */
	isCollapsed?: boolean;
}) => {
	const triggerRef = useRef<HTMLDivElement>(null);
	const [isOpenUncontrolled, setIsOpenUncontrolled] = useState(false);
	const isControlled = isOpenControlled !== undefined;
	const isMenuOpen = isControlled ? isOpenControlled : isOpenUncontrolled;

	function handleOpenChange(next: boolean) {
		if (!isControlled) setIsOpenUncontrolled(next);
		onOpenChange?.(next);
	}

	const isBrand = variant === "brand";
	const isOrgBrand = isBrand && menuType === "organization";
	const showTrailingChevron =
		menuType === "organization" || accountMode !== AccountModeConst.Standard;
	const TrailingChevron =
		menuType === "organization" ? ChevronDown : ChevronSelectorVertical;

	/** Figma 3025:3109 Org Switcher — h-44, px-sm py-xs, gap 10 */
	const brandOrgPillClass = cx(
		"h-11 rounded-full",
		/* Inset stroke — avoids double/clipped edge vs parent overflow-hidden */
		"border-0 bg-alpha-white-10 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)]",
		"outline-none ring-0",
		"px-(--spacing-sm,6px) py-(--spacing-xs,4px)",
		"gap-2.5",
	);
	/** Figma 3025:3095 / 3025:3052 account cards — h-54, px-lg py-md (Portal + Standard) */
	const brandAccountPillClass = cx(
		"h-[54px] rounded-full",
		"border-0 bg-alpha-white-10 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)]",
		"outline-none ring-0",
		"px-(--spacing-lg,12px) py-(--spacing-md,8px)",
		"gap-2.5",
	);
	const brandPillClass = isOrgBrand ? brandOrgPillClass : brandAccountPillClass;
	/**
	 * Figma Nav footer 3940:41078 / NavAccountCard 3940:41079 — collapsed.
	 * Must be a true square (`size-*`) so `rounded-full` is a circle.
	 */
	const brandCollapsedPillClass =
		menuType === "organization"
			? "flex size-11 shrink-0 items-center justify-center rounded-full border-0 bg-alpha-white-10 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)] outline-none ring-0"
			: "flex size-[54px] shrink-0 items-center justify-center rounded-full border-0 bg-alpha-white-10 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)] outline-none ring-0";
	const brandPillInteractiveClass =
		"hover:bg-alpha-white-20 pressed:bg-alpha-white-20 focus-visible:outline-none focus-visible:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1),0_0_0_2px_#ffffff,0_0_0_4px_#213a53]";
	const brandPillOpenClass = "bg-alpha-white-20";
	/**
	 * Figma AvatarLabelGroup on brand — gap spacing-md, text start-aligned.
	 * Shared by Org + Account (Standard / Portal / Application).
	 */
	const brandLabelGroupClass =
		"h-auto min-w-0 flex-1 justify-start gap-(--spacing-md,8px) text-left";
	/** Figma Text sm/Extrabold + primary_on-brand */
	const brandTitleClass =
		"min-w-0 w-full overflow-hidden text-left text-sm font-extrabold leading-5 text-ellipsis whitespace-nowrap text-[rgba(255,255,255,0.9)]";
	/** Figma Text xs/Medium + tertiary_on-brand */
	const brandSubtitleClass =
		"text-left text-xs font-medium leading-[18px] text-[rgba(255,255,255,0.6)]";
	/** Match extrabold label weight on SVG placeholder icons */
	const brandIconClass = cx(
		iconClassName,
		"text-[rgba(255,255,255,0.9)] stroke-[2.5px]",
	);

	const avatarProps = {
		src: typeof avatarSrc === "string" ? avatarSrc : undefined,
		placeholderIcon: typeof avatarSrc === "function" ? avatarSrc : undefined,
		iconClassName: isBrand ? brandIconClass : iconClassName,
		alt: avatarAlt,
		initials: avatarInitials,
		backgroundColor: avatarBackgroundColor,
		contrastBorder: false as const,
		size: "sm" as const,
	};

	const menu = (
		<NavMenu
			selectedId={selectedId}
			avatarSrc={avatarSrc}
			title={title}
			avatarAlt={avatarAlt}
			avatarInitials={avatarInitials}
			menuType={menuType}
			availableOrganizations={availableOrganizations}
			currentOrganization={currentOrganization}
			mainAppNavCardOnClickHandler={mainAppNavCardOnClickHandler}
			adminAppNavCardOnClickHandler={adminAppNavCardOnClickHandler}
			isPlatformAdmin={isPlatformAdmin}
			isOrgAdmin={isOrgAdmin}
			accountMode={accountMode}
			onUserProfile={onUserProfile}
			onSignOut={onSignOut}
			disabled={disabled}
		/>
	);

	const popoverClassName = ({
		isEntering,
		isExiting,
	}: {
		isEntering: boolean;
		isExiting: boolean;
	}) =>
		cx(
			isCollapsed
				? "min-w-[240px] origin-(--trigger-anchor-point) will-change-transform"
				: "w-(--trigger-width) origin-(--trigger-anchor-point) will-change-transform",
			isEntering &&
				"duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5",
			isExiting &&
				"duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5",
		);

	if (!showSelector) {
		// Static (non-clickable) version
		if (isCollapsed) {
			return (
				<div
					ref={triggerRef}
					className={cx(
						"relative mx-auto",
						isBrand
							? brandCollapsedPillClass
							: "flex items-center rounded-full p-1",
					)}
				>
					<Avatar {...avatarProps} />
				</div>
			);
		}

		return (
			<div
				ref={triggerRef}
				className={cx(
					"relative flex w-full mx-auto items-center text-left",
					isBrand
						? cx(
								brandPillClass,
								isOrgBrand ? "justify-start" : "justify-between",
							)
						: "justify-between gap-2.5 rounded-xl p-3 ring-1 ring-secondary text-fg-quaternary",
				)}
			>
				<AvatarLabelGroup
					size="sm"
					src={avatarProps.src}
					placeholderIcon={avatarProps.placeholderIcon}
					iconClassName={avatarProps.iconClassName}
					title={title}
					subtitle={subtitle}
					alt={avatarAlt}
					initials={avatarInitials}
					backgroundColor={avatarBackgroundColor}
					className={isBrand ? brandLabelGroupClass : undefined}
					titleClassName={isBrand ? brandTitleClass : undefined}
					subtitleClassName={isBrand ? brandSubtitleClass : undefined}
				/>
			</div>
		);
	}

	// Collapsed interactive mark (Figma Nav footer 3940:41078)
	if (isCollapsed) {
		const collapsedPlacement = popoverPlacement ?? ("right" as Placement);

		return (
			<AriaDialogTrigger isOpen={isMenuOpen} onOpenChange={handleOpenChange}>
				<AriaButton
					aria-expanded={isMenuOpen}
					aria-label={avatarAlt || title}
					className={cx(
						"relative mx-auto cursor-pointer transition duration-100 ease-linear",
						isBrand
							? cx(
									brandCollapsedPillClass,
									brandPillInteractiveClass,
									isMenuOpen && brandPillOpenClass,
								)
							: "flex items-center rounded-full p-1 outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2",
						disabled && "cursor-not-allowed opacity-50",
					)}
					isDisabled={disabled}
				>
					<Avatar {...avatarProps} />
				</AriaButton>

				<AriaPopover
					placement={collapsedPlacement}
					offset={8}
					className={popoverClassName}
				>
					{menu}
				</AriaPopover>
			</AriaDialogTrigger>
		);
	}

	// Interactive (clickable) expanded version
	/** Org: single-line title. Account (Standard/Portal/Application): title + optional subtitle. */
	const brandTriggerContent = (
		<>
			<span className="flex min-w-0 flex-1 items-center gap-(--spacing-md,8px)">
				<Avatar {...avatarProps} className="shrink-0" />
				<span className="flex min-w-0 flex-1 flex-col items-start justify-center">
					<span className={brandTitleClass}>{title}</span>
					{subtitle ? (
						<span className={cx("min-w-0 w-full truncate", brandSubtitleClass)}>
							{subtitle}
						</span>
					) : null}
				</span>
			</span>
			{showTrailingChevron && (
				<TrailingChevron className="ml-auto size-5 shrink-0 self-center text-[rgba(255,255,255,0.9)]" />
			)}
		</>
	);

	const lightTriggerContent = (
		<>
			<AvatarLabelGroup
				size="sm"
				src={avatarProps.src}
				placeholderIcon={avatarProps.placeholderIcon}
				iconClassName={iconClassName}
				title={title}
				subtitle={subtitle}
				alt={avatarAlt}
				initials={avatarInitials}
				backgroundColor={avatarBackgroundColor}
			/>
			{showTrailingChevron && (
				<TrailingChevron className="size-5 shrink-0 self-center text-fg-quaternary" />
			)}
		</>
	);

	return (
		<div ref={triggerRef} className="w-full">
			<AriaDialogTrigger isOpen={isMenuOpen} onOpenChange={handleOpenChange}>
				<AriaButton
					aria-expanded={isMenuOpen}
					className={cx(
						"relative flex w-full mx-auto items-center transition duration-100 ease-linear",
						/* UA/RAC button defaults to text-align:center — forces label mid-pill */
						"cursor-pointer text-left",
						isBrand ? "justify-start" : "justify-between gap-2.5",
						isBrand
							? cx(
									brandPillClass,
									brandPillInteractiveClass,
									isMenuOpen && brandPillOpenClass,
								)
							: "rounded-xl px-3 py-2 ring-1 ring-secondary outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2 hover:bg-primary_hover hover:text-fg-quaternary_hover pressed:bg-primary_hover pressed:text-fg-quaternary_hover",
						disabled && "cursor-not-allowed opacity-50",
					)}
					isDisabled={disabled}
				>
					{isBrand ? brandTriggerContent : lightTriggerContent}
				</AriaButton>

				<AriaPopover
					placement={
						popoverPlacement ?? (menuType === "organization" ? "bottom" : "top")
					}
					triggerRef={triggerRef}
					offset={8}
					className={popoverClassName}
				>
					{menu}
				</AriaPopover>
			</AriaDialogTrigger>
		</div>
	);
};
